import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCurrentDocument } from '~/entities/document'
import {
  AuditTrail,
  LoadingSkeleton as AuditTrailLoadingSkeleton,
  useAuditTrail,
} from '~/features/audit-trail'
import type { StepName } from '~/features/document-analytics'
import { StepSessionProvider } from '~/features/document-analytics'
import { registerPdfTab } from '~/features/pdf-tab-reminder'
import { cn } from '~/shared/lib/cn'
import { useElementWidth } from '~/shared/lib/use-element-width'
import { LocalIcon, PDFLoadingSkeleton, Text, Tooltip } from '~/shared/ui'
import { DocumentPDF } from './document-pdf'
import { PdfCollapsedProvider } from './pdf-collapsed-context'
import { LoadingSkeleton } from './review-form/loading-skeleton'
import { ReviewForm } from './review-form/review-form'

const IDLE_TIMEOUT_MS = 5000
const WORKSPACE_WIDTH = 830
const AUDIT_TRAIL_WIDTH = 480
const PDF_COLLAPSE_THRESHOLD = 360

interface DocumentReviewLayoutProps extends React.PropsWithChildren {
  step:
    | 'Classification'
    | 'Data Extraction'
    | 'Tagging'
    | 'Payment Deposit'
    | 'Duplication'
    | 'EOB Processing'
  loader?: React.ReactNode
  actions?: React.ReactNode
  customScroll?: boolean
}

export function DocumentReviewLayout({
  children,
  step,
  loader,
  actions,
  customScroll,
}: DocumentReviewLayoutProps) {
  const {
    data: { document },
  } = useCurrentDocument()
  const { isAuditTrailVisible } = useAuditTrail()

  const containerRef = useRef<HTMLDivElement>(null)
  const containerWidth = useElementWidth(containerRef)

  // Width-driven collapse: when the Document panel cannot fit ≥ 360 px after
  // reserving Workspace (830) and Audit Trail (480 if open) it collapses to a
  // narrow rail. Treat the initial 0-width measurement as "not yet ready" so
  // we don't flash the rail while ResizeObserver settles.
  const widthDerivedCollapsed =
    containerWidth > 0 &&
    containerWidth - WORKSPACE_WIDTH - (isAuditTrailVisible ? AUDIT_TRAIL_WIDTH : 0) <
      PDF_COLLAPSE_THRESHOLD

  // User override — `null` means "follow width". When the width-derived value
  // flips, reset the override so the layout responds to viewport / AT changes.
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null)
  const previousWidthDerivedRef = useRef(widthDerivedCollapsed)
  useEffect(() => {
    if (previousWidthDerivedRef.current !== widthDerivedCollapsed) {
      previousWidthDerivedRef.current = widthDerivedCollapsed
      setUserCollapsed(null)
    }
  }, [widthDerivedCollapsed])

  const isPdfCollapsed = userCollapsed ?? widthDerivedCollapsed

  // Expand chip is disabled only while the user can't physically expand:
  // collapsed AND audit trail open AND the math forces collapse. Otherwise
  // (AT closed, or width allows expansion) the chip stays clickable.
  const isExpandDisabled = isPdfCollapsed && isAuditTrailVisible && widthDerivedCollapsed

  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const resetIdleTimer = useCallback(() => {
    clearIdleTimer()
    setIsIdle(false)
    idleTimerRef.current = setTimeout(() => setIsIdle(true), IDLE_TIMEOUT_MS)
  }, [clearIdleTimer])

  useEffect(() => {
    return () => clearIdleTimer()
  }, [clearIdleTimer])

  const stepNameMap: Record<DocumentReviewLayoutProps['step'], StepName> = {
    Classification: 'classification',
    'Data Extraction': 'data_extraction',
    Tagging: 'tagging',
    'Payment Deposit': 'eob_payment_deposit',
    Duplication: 'eob_duplication',
    'EOB Processing': 'eob_processing',
  }

  const auditTrailPage = useMemo(() => {
    switch (step) {
      case 'Classification':
      default:
        return 'classification'
      case 'Data Extraction':
        return 'data'
      case 'Tagging':
        return 'tagging'
      case 'Payment Deposit':
        return 'eob-payment-deposit'
      case 'Duplication':
        return 'eob-duplication'
      case 'EOB Processing':
        return 'eob-processing'
    }
  }, [step])

  const showCollapsed = isPdfCollapsed

  return (
    <StepSessionProvider
      step={stepNameMap[step]}
      documentKey={document.key}
    >
      <PdfCollapsedProvider value={isPdfCollapsed}>
        <div
          ref={containerRef}
          className="flex h-full w-full min-w-[1280px] overflow-hidden"
        >
          <div
            className={cn('group relative h-full min-w-0', {
              'bg-surface-high-inverse flex w-14 shrink-0 grow-0 flex-col items-center justify-between py-2':
                showCollapsed,
              'max-w-full grow overflow-hidden': !showCollapsed,
            })}
            onMouseEnter={() => resetIdleTimer()}
            onMouseLeave={() => {
              clearIdleTimer()
              setIsIdle(false)
            }}
          >
            <div
              className={cn('h-full min-w-0 grow overflow-hidden', {
                hidden: showCollapsed,
              })}
            >
              <Suspense fallback={<PDFLoadingSkeleton variant="document" />}>
                <DocumentPDF
                  className={cn('max-w-[calc(max(100dvw,1280px)-830px)]', {
                    'max-w-[calc(max(100dvw,1280px)-830px-480px)]': isAuditTrailVisible,
                  })}
                  documentKey={document.key}
                  initialFitToWidth
                />
              </Suspense>
            </div>

            {(() => {
              const toggle = (
                <div
                  className={cn(
                    'bg-content-highest-emphasis text-content-highest-emphasis-inverse flex shrink-0 items-center gap-2 rounded-lg p-2 transition-opacity',
                    {
                      'cursor-pointer': !isExpandDisabled,
                      'cursor-not-allowed opacity-50': isExpandDisabled,
                      'absolute top-4 right-4': !isPdfCollapsed,
                      'pointer-events-none opacity-0': !isPdfCollapsed && isIdle,
                      'opacity-0 group-hover:pointer-events-auto group-hover:opacity-100':
                        !isPdfCollapsed && !isIdle,
                    },
                  )}
                  onClick={() => {
                    if (isExpandDisabled) return
                    setUserCollapsed(!isPdfCollapsed)
                  }}
                >
                  <LocalIcon
                    className={cn({ 'rotate-180': isPdfCollapsed })}
                    name="menuCollapseOutlined"
                  />
                  {!isPdfCollapsed && <Text appearance="BODY2">Collapse Document</Text>}
                </div>
              )

              if (isExpandDisabled) {
                return <Tooltip content="Close Audit Trail to expand document">{toggle}</Tooltip>
              }

              return toggle
            })()}

            {showCollapsed && (
              <Tooltip content="Open in new tab">
                <div
                  className="bg-content-highest-emphasis text-content-highest-emphasis-inverse flex cursor-pointer rounded-lg p-2"
                  onClick={() => {
                    const win = window.open(`/documents/${document.key}/view-pdf`, '_blank')
                    registerPdfTab(document.key, win)
                  }}
                >
                  <LocalIcon name="openInNew" />
                </div>
              </Tooltip>
            )}
          </div>

          <div
            className={cn('border-divider h-full border-l', {
              'min-w-0 grow': showCollapsed,
              'w-[830px] shrink-0': !showCollapsed,
            })}
          >
            <Suspense fallback={<LoadingSkeleton loader={loader} />}>
              <ReviewForm
                step={step}
                loader={loader}
                actions={actions}
                customScroll={customScroll}
              >
                {children}
              </ReviewForm>
            </Suspense>
          </div>

          {isAuditTrailVisible && (
            <Suspense fallback={<AuditTrailLoadingSkeleton />}>
              <AuditTrail
                page={auditTrailPage}
                assetId={document.key}
              />
            </Suspense>
          )}
        </div>
      </PdfCollapsedProvider>
    </StepSessionProvider>
  )
}
